import { shipmentContactInputSchema } from "../shipment-contact.schema";
import { readBulkRecipientUnit } from "./bulk-recipient-unit";
import { ShippingTextError } from "@/lib/shipping-text";
import {
  validateBulkShippingText,
  bulkShippingContactMessage,
  bulkShippingContactError,
  updateBulkShippingRow,
  bulkShippingTextMessage,
  mergeBulkShippingRow,
} from "./bulk-shipping-validation";
import { BulkShipmentResults } from "./bulk-shipment-result";
import { ADDRESS_LOOKUP_LANGUAGE } from "@/services/address-lookup-language";
import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import axios from "axios";
import { utils, read } from "xlsx";
import {
  Button,
  Card,
  CardBody,
  Checkbox,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
} from "@heroui/react";
import {
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertCircle,
  Zap,
  Truck,
  X,
  ChevronDown,
  ChevronUp,
  MapPin,
  CreditCard,
  FileText,
} from "lucide-react";
import { useSession } from "@/services/auth";
import { useCustomerContacts } from "@/features/address-book/api";
import { useCalculateQuoteMutation } from "@/api/use-shipment-quotes";
import { useCreateUnifiedOrder } from "@/api/use-unified-order";
import {
  usePaymentMethods,
  useMyPaymentTerms,
} from "@/api/use-payment-methods-api";
import { useToast } from "@/hooks/use-toast";
import {
  type PricingOption,
  PaymentMethodType,
  type Order,
  type PickupTimeSlot,
} from "@/api.types.v2";
import { useTranslation } from "react-i18next";

// Bulk view shows a single pickup time-slot list shared across all rows. The
// server's new shape carries slots per pickup tier (per quote option), so we
// derive a synthesized list from the first quote's first tier — bulk doesn't
// pick a tier per row, so this matches existing UX. Per-row pricing-step UI
// is where per-tier slot scoping actually matters.
interface BulkPickupConfig {
  timeSlots: PickupTimeSlot[];
}

// Template headers for spreadsheet
const TEMPLATE_HEADERS = [
  "Recipient Name",
  "Recipient Phone",
  "Recipient Email",
  "Recipient Address",
  "Recipient Unit",
  "Recipient City",
  "Recipient Province",
  "Recipient Postal Code",
  "Package Weight",
  "Weight Unit",
  "Package Length",
  "Package Width",
  "Package Height",
  "Dimension Unit",
  "Delivery Instructions",
];

// Unit types
type WeightUnit = "kg" | "lb" | "oz" | "g";
type DimensionUnit = "cm" | "in";

// Format phone number to Canadian format with +1 prefix
const formatCanadianPhone = (phone: string): string => {
  if (!phone) return "";
  // Remove all non-digits
  const digits = phone.replace(/\D/g, "");
  // If 10 digits, add +1 prefix
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  // If 11 digits starting with 1, add + prefix
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+${digits}`;
  }
  // If already has +, return as-is with only digits after +
  if (phone.startsWith("+")) {
    return `+${digits}`;
  }
  // Return original if unclear format
  return phone;
};

// Order status types
type OrderStatus =
  | "pending"
  | "validating"
  | "ready"
  | "fetching_quote"
  | "needs_fix"
  | "processing"
  | "completed"
  | "error"
  | "skipped";

// Parsed order from spreadsheet
export interface ParsedOrder {
  id: string;
  recipientName: string;
  recipientPhone: string;
  recipientEmail: string;
  recipientAddress: string;
  recipientUnit: string;
  recipientCity: string;
  recipientProvince: string;
  recipientPostalCode: string;
  weight: number;
  weightUnit: WeightUnit;
  length: number;
  width: number;
  height: number;
  dimensionUnit: DimensionUnit;
  deliveryNotes: string;
  status: OrderStatus;
  selected: boolean; // Whether this order is selected for shipping
  validatedAddress?: {
    street1: string;
    street2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    latitude: number;
    longitude: number;
  };
  errorMessage?: string;
  availableQuotes?: PricingOption[];
  selectedQuote?: PricingOption;
  quoteId?: string;
  createdOrder?: Order;
  /**
   * Per-row pickup. null = drop-off. Set explicitly only when the user
   * chose "Same pickup for all rows" or used the per-row edit popover.
   */
  pickup?: { date: string; slotId: string } | null;
}

type PickupMode = "none" | "same" | "custom";

// Phases of the bulk ship flow
type Phase = "upload" | "review" | "complete";

// Payment choice type
type PaymentChoice = "SAVED_CARD" | "DECIDE_LATER" | "INVOICE";

interface BulkShipFormProps {
  onClose?: () => void;
}

export function BulkShipForm({ onClose }: BulkShipFormProps) {
  const { toast } = useToast();
  const { t, i18n } = useTranslation("app");
  const locale = i18n.resolvedLanguage ?? i18n.language;
  useSession();
  const { data: contactsData, isLoading: isLoadingContacts } =
    useCustomerContacts();
  const contacts = contactsData?.contacts ?? [];
  const { data: billingData } = usePaymentMethods();
  const { data: paymentTermsData } = useMyPaymentTerms();

  // Net terms data
  const hasNetTermsAccess = paymentTermsData?.hasNetTermsAccess ?? false;
  const availableCredit = paymentTermsData?.availableCredit ?? 0;
  const paymentTerms = paymentTermsData?.paymentTerms ?? [];
  const hasUnpaidInvoices = paymentTermsData?.hasUnpaidInvoices ?? false;

  // State
  const [phase, setPhase] = useState<Phase>("upload");
  const [orders, setOrders] = useState<ParsedOrder[]>([]);
  const [currentOrderIndex, setCurrentOrderIndex] = useState<number | null>(
    null
  );
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [isValidatingAll, setIsValidatingAll] = useState(false);
  const [isFetchingQuotes, setIsFetchingQuotes] = useState(false);

  // Selected sender address
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    null
  );

  // Pickup configuration. Synthesized from the first quote's first pickup
  // tier (sender is shared, slots are now per-tier; bulk view doesn't pick
  // a tier per row, so the first tier's slot list is the canonical bulk set).
  // The mode + same-mode default flow into per-row order.pickup state.
  const [pickupConfig, setPickupConfig] = useState<BulkPickupConfig | null>(
    null
  );
  const [pickupMode, setPickupMode] = useState<PickupMode>("none");
  const [defaultPickup, setDefaultPickup] = useState<{
    date: string;
    slotId: string;
  } | null>(null);

  // Per-row edit popover state
  const [editPickupForOrderId, setEditPickupForOrderId] = useState<
    string | null
  >(null);

  // Mode → per-row sync. Switching mode rewrites every row's pickup; "custom"
  // keeps existing per-row state so users can refine without losing edits.
  useEffect(() => {
    if (pickupMode === "none") {
      setOrders((prev) => prev.map((o) => ({ ...o, pickup: null })));
    } else if (pickupMode === "same") {
      setOrders((prev) =>
        prev.map((o) => ({ ...o, pickup: defaultPickup ?? null }))
      );
    }
    // "custom" intentionally does not sync — user owns each row.
  }, [pickupMode, defaultPickup]);

  // Payment selection
  const [paymentChoice, setPaymentChoice] =
    useState<PaymentChoice>("SAVED_CARD");
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null);

  // File upload state
  const [isDragging, setIsDragging] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);

  // Package edit state - tracks pending changes before confirmation
  const [packageEdits, setPackageEdits] = useState<
    Record<
      string,
      {
        weight: number;
        weightUnit: WeightUnit;
        length: number;
        width: number;
        height: number;
        dimensionUnit: DimensionUnit;
        deliveryNotes: string;
      }
    >
  >({});

  // Get default contact or first contact
  const defaultContact = contacts.find((c) => c.isDefault) ?? contacts[0];
  const selectedContact = selectedAddressId
    ? contacts.find((c) => c.id === selectedAddressId)
    : defaultContact;

  // Auto-select default contact when contacts load
  useEffect(() => {
    if (!selectedAddressId && defaultContact?.id) {
      setSelectedAddressId(defaultContact.id);
    }
  }, [defaultContact?.id, selectedAddressId]);

  // Memoized so downstream useCallback/useEffect dependency arrays get a
  // stable reference when the contact fields haven't changed.
  const senderAddress = useMemo(
    () =>
      selectedContact
        ? {
            name: selectedContact.contactName || "",
            phone: selectedContact.contactPhone ?? "",
            email: selectedContact.contactEmail,
            street1: selectedContact.addressLine1 || "",
            street2: selectedContact.addressLine2 ?? "",
            city: selectedContact.addressCity || "",
            state: selectedContact.addressProvince || "",
            postalCode: selectedContact.addressPostalCode ?? "",
            country: selectedContact.addressCountry,
            latitude: selectedContact.location?.lat,
            longitude: selectedContact.location?.lng,
          }
        : null,
    [selectedContact]
  );

  // Payment methods
  const paymentMethods = billingData?.paymentMethods ?? [];
  const defaultPaymentMethod = paymentMethods.find((pm) => pm.isDefault);
  const selectedPaymentMethod = selectedPaymentMethodId
    ? paymentMethods.find((pm) => pm.id === selectedPaymentMethodId)
    : defaultPaymentMethod;

  // Use first available payment terms (most customers only have one)
  const effectivePaymentTermsId = paymentTerms[0]?.id || null;

  // Auto-select default payment method when loaded
  useEffect(() => {
    if (!selectedPaymentMethodId && defaultPaymentMethod?.id) {
      setSelectedPaymentMethodId(defaultPaymentMethod.id);
    }
  }, [defaultPaymentMethod?.id, selectedPaymentMethodId]);

  // Mutations
  const calculateQuote = useCalculateQuoteMutation();
  const createOrder = useCreateUnifiedOrder();

  // Stats
  const readyCount = orders.filter(
    (o) => o.status === "ready" && o.selectedQuote && o.selected
  ).length;
  const needsFixCount = orders.filter((o) => o.status === "needs_fix").length;
  const completedCount = orders.filter((o) => o.status === "completed").length;
  const fetchingQuoteCount = orders.filter(
    (o) =>
      o.status === "fetching_quote" ||
      (o.status === "ready" && !o.selectedQuote)
  ).length;
  const allSelectableOrders = orders.filter(
    (o) => o.status === "ready" && o.selectedQuote
  );
  const allSelected =
    allSelectableOrders.length > 0 &&
    allSelectableOrders.every((o) => o.selected);

  // Download template
  const handleDownloadTemplate = () => {
    const wb = utils.book_new();
    const exampleRow = [
      "John Smith",
      "4165551234",
      "john@example.com",
      "123 Main Street",
      "Unit 4B",
      "Toronto",
      "ON",
      "M5V 2K4",
      "2", // Package Weight
      "kg", // Weight Unit (kg, lb, oz, g)
      "30", // Package Length
      "20", // Package Width
      "15", // Package Height
      "cm", // Dimension Unit (cm, in)
      "Leave at door",
    ];
    const ws = utils.aoa_to_sheet([TEMPLATE_HEADERS, exampleRow]);
    utils.book_append_sheet(wb, ws, "Orders");

    void import("xlsx").then(({ writeFileXLSX }) => {
      writeFileXLSX(wb, "bulk-ship-template.xlsx");
    });
  };

  // Helper to find column value with flexible header matching
  const getColumnValue = (
    row: Record<string, string>,
    ...possibleHeaders: string[]
  ): string => {
    for (const header of possibleHeaders) {
      // Try exact match first
      if (row[header] !== undefined) return String(row[header]);
      // Try case-insensitive partial match
      const key = Object.keys(row).find((k) =>
        k.toLowerCase().includes(header.toLowerCase().replace(/[^a-z]/g, ""))
      );
      if (key && row[key] !== undefined) return String(row[key]);
    }
    return "";
  };

  // Parse uploaded file
  const handleFileUpload = async (file: File) => {
    try {
      const data = await file.arrayBuffer();
      const workbook = read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = utils.sheet_to_json<Record<string, string>>(worksheet);

      // Helper to parse weight unit
      const parseWeightUnit = (unit: string): WeightUnit => {
        const normalized = unit.toLowerCase().trim();
        if (
          normalized === "lb" ||
          normalized === "lbs" ||
          normalized === "pound" ||
          normalized === "pounds"
        )
          return "lb";
        if (
          normalized === "oz" ||
          normalized === "ounce" ||
          normalized === "ounces"
        )
          return "oz";
        if (
          normalized === "g" ||
          normalized === "gram" ||
          normalized === "grams"
        )
          return "g";
        return "kg"; // default
      };

      // Helper to parse dimension unit
      const parseDimensionUnit = (unit: string): DimensionUnit => {
        const normalized = unit.toLowerCase().trim();
        if (
          normalized === "in" ||
          normalized === "inch" ||
          normalized === "inches"
        )
          return "in";
        return "cm"; // default
      };

      const parsedOrders: ParsedOrder[] = jsonData
        .filter(
          (row) =>
            getColumnValue(row, "Recipient Name") ||
            getColumnValue(row, "Recipient Address")
        )
        .map((row, index) => ({
          id: `order-${index}-${Date.now()}`,
          recipientName: getColumnValue(row, "Recipient Name"),
          recipientPhone: formatCanadianPhone(
            getColumnValue(row, "Recipient Phone")
          ),
          recipientEmail: getColumnValue(row, "Recipient Email"),
          recipientAddress: getColumnValue(row, "Recipient Address"),
          recipientUnit: readBulkRecipientUnit(row),
          recipientCity: getColumnValue(row, "Recipient City", "City"),
          recipientProvince: getColumnValue(
            row,
            "Recipient Province",
            "Province",
            "State"
          ),
          recipientPostalCode: getColumnValue(
            row,
            "Recipient Postal Code",
            "Postal Code",
            "Zip"
          ),
          weight: Number(getColumnValue(row, "Package Weight", "Weight")) || 1,
          weightUnit: parseWeightUnit(getColumnValue(row, "Weight Unit")),
          length: Number(getColumnValue(row, "Package Length", "Length")) || 30,
          width: Number(getColumnValue(row, "Package Width", "Width")) || 20,
          height: Number(getColumnValue(row, "Package Height", "Height")) || 15,
          dimensionUnit: parseDimensionUnit(
            getColumnValue(row, "Dimension Unit")
          ),
          deliveryNotes: getColumnValue(
            row,
            "Delivery Instructions",
            "Delivery Notes"
          ),
          status: "pending" as OrderStatus,
          selected: true,
        }));

      if (parsedOrders.length === 0) {
        toast({
          title: t("bulkShip.noOrdersTitle"),
          description: t("bulkShip.noOrdersBody"),
          variant: "destructive",
        });
        return;
      }

      setOrders(parsedOrders);
      setPhase("review");

      // Start validating all addresses
      void validateAllAddresses(parsedOrders);
    } catch (error) {
      console.error("Error parsing file:", error);
      toast({
        title: t("bulkShip.parseErrorTitle"),
        description: t("bulkShip.parseErrorBody"),
        variant: "destructive",
      });
    }
  };

  // Handle file drop
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && /\.(csv|xlsx|xls)$/i.test(file.name)) {
      void handleFileUpload(file);
    } else {
      setIsInvalid(true);
    }
  };

  // Handle file input change
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      void handleFileUpload(file);
    }
  };

  // Validate all addresses using Google Geocoder
  const validateAllAddresses = async (ordersToValidate: ParsedOrder[]) => {
    if (typeof google === "undefined" || !google.maps?.Geocoder) {
      const ids = new Set(ordersToValidate.map((order) => order.id));
      setOrders((current) =>
        current.map((order) =>
          ids.has(order.id)
            ? {
                ...order,
                status: "needs_fix",
                errorMessage: t("bulkShip.addressLookupUnavailable"),
              }
            : order
        )
      );
      return;
    }
    setIsValidatingAll(true);
    const geocoder = new google.maps.Geocoder();

    const validateAddress = (order: ParsedOrder): Promise<ParsedOrder> => {
      return new Promise((resolve) => {
        const textError = bulkShippingTextMessage(
          validateBulkShippingText(order),
          t
        );
        const contactError = bulkShippingContactMessage(order, t);
        if (textError || contactError) {
          resolve({
            ...order,
            status: "needs_fix",
            errorMessage: textError ?? contactError,
          });
          return;
        }
        const addressString = [
          order.recipientAddress,
          order.recipientCity,
          order.recipientProvince,
          order.recipientPostalCode,
          "Canada",
        ]
          .filter(Boolean)
          .join(", ");

        void geocoder.geocode(
          {
            address: addressString,
            language: ADDRESS_LOOKUP_LANGUAGE,
            region: "CA",
          },
          (results, status) => {
            if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
              const result = results[0];
              const components = result.address_components || [];

              const getComponent = (type: string, useShort = false) => {
                const comp = components.find((c) => c.types.includes(type));
                return useShort ? comp?.short_name : comp?.long_name;
              };

              const streetNumber = getComponent("street_number") ?? "";
              const route = getComponent("route") ?? "";

              // Format postal code to Canadian standard (A1A 1A1)
              // Google sometimes returns partial postal codes (e.g., "K4P" instead of "K4P 1J1")
              // Use original postal code from CSV if geocoder returns incomplete one
              const geocodedPostalCode = getComponent("postal_code") ?? "";
              const formatPostalCode = (pc: string) => {
                const clean = pc.replace(/\s/g, "").toUpperCase();
                if (clean.length === 6) {
                  return `${clean.slice(0, 3)} ${clean.slice(3)}`;
                }
                return pc;
              };
              // If geocoded postal code is incomplete (less than 6 chars), use original from CSV
              const rawPostalCode =
                geocodedPostalCode.replace(/\s/g, "").length >= 6
                  ? geocodedPostalCode
                  : order.recipientPostalCode;

              resolve({
                ...order,
                status: "fetching_quote",
                validatedAddress: {
                  street1:
                    streetNumber && route
                      ? `${streetNumber} ${route}`
                      : order.recipientAddress,
                  street2: order.recipientUnit || undefined,
                  city:
                    getComponent("locality") ??
                    getComponent("sublocality") ??
                    order.recipientCity,
                  state:
                    getComponent("administrative_area_level_1", true) ??
                    order.recipientProvince,
                  postalCode: formatPostalCode(rawPostalCode),
                  country: "CA",
                  latitude: result.geometry?.location?.lat() ?? 0,
                  longitude: result.geometry?.location?.lng() ?? 0,
                },
              });
            } else {
              resolve({
                ...order,
                status: "needs_fix",
                errorMessage: t("bulkShip.verifyAddressError"),
              });
            }
          }
        );
      });
    };

    // Validate in batches of 5 to avoid rate limiting
    const batchSize = 5;

    for (let i = 0; i < ordersToValidate.length; i += batchSize) {
      const batch = ordersToValidate.slice(i, i + batchSize);
      const validated = await Promise.all(batch.map(validateAddress));

      // Apply only this completed batch; earlier quotes may already be ready.
      setOrders((current) =>
        validated.reduce((rows, result, index) => {
          const source = batch[index];
          return source ? mergeBulkShippingRow(rows, source, result) : rows;
        }, current)
      );
    }

    setIsValidatingAll(false);

    // The existing quote effect processes the merged rows, including retries.
  };

  // Fetch quotes for orders
  const fetchQuotesForOrders = useCallback(
    async (ordersToQuote: ParsedOrder[]) => {
      if (!senderAddress) return;

      setIsFetchingQuotes(true);

      for (const order of ordersToQuote) {
        if (!order.validatedAddress) continue;

        let result: ParsedOrder;
        try {
          const response = await calculateQuote.mutateAsync({
            senderName: senderAddress.name,
            senderPhone: senderAddress.phone,
            senderEmail: "",
            shipFrom: {
              street1: senderAddress.street1,
              street2: senderAddress.street2,
              city: senderAddress.city,
              state: senderAddress.state,
              postalCode: senderAddress.postalCode,
              country: senderAddress.country,
            },
            recipientName: order.recipientName,
            recipientPhone: order.recipientPhone,
            recipientEmail: "",
            shipTo: order.validatedAddress,
            packages: [
              {
                weight: order.weight,
                weightUnit: order.weightUnit,
                length: order.length,
                width: order.width,
                height: order.height,
                dimensionUnit: order.dimensionUnit,
              },
            ],
            deliveryNotes: order.deliveryNotes,
          });

          // Capture pickupConfig once (same for all rows since sender is shared).
          // Derive from the first option's first pickup tier — server moved
          // slots to per-tier; bulk view collapses to a single shared list.
          if (!pickupConfig) {
            const firstTier = response.data.options
              .flatMap((o) => o.pickupOptions)
              .find((t) => t.timeSlots.length > 0);
            if (firstTier) {
              setPickupConfig({ timeSlots: firstTier.timeSlots });
            }
          }

          const options = response.data.options;
          if (options.length === 0) continue;
          const selectedQuote = options
            .slice()
            .sort(
              (a, b) =>
                (a.carrierService.estimatedDays ?? 999) -
                (b.carrierService.estimatedDays ?? 999)
            )[0];
          result = {
            ...order,
            status: "ready",
            availableQuotes: options,
            selectedQuote,
            quoteId: `${selectedQuote.carrier.id}__${selectedQuote.carrierService.code}`,
          };
        } catch (error) {
          console.error("Error fetching quote for order:", order.id, error);
          result = {
            ...order,
            status: "needs_fix",
            errorMessage:
              error instanceof ShippingTextError
                ? error.localizedMessage(t)
                : t("bulkShip.pricingError"),
          };
        }
        setOrders((current) => mergeBulkShippingRow(current, order, result));
      }

      setIsFetchingQuotes(false);
    },
    [senderAddress, calculateQuote]
  );

  // Trigger quote fetch when sender address becomes available and orders are waiting
  useEffect(() => {
    if (!senderAddress || isFetchingQuotes) return;

    const ordersNeedingQuotes = orders.filter(
      (o) => o.status === "fetching_quote"
    );
    if (ordersNeedingQuotes.length > 0) {
      void fetchQuotesForOrders(ordersNeedingQuotes);
    }
  }, [senderAddress, orders, isFetchingQuotes, fetchQuotesForOrders]);

  // Update order field
  const updateOrderField = <K extends keyof ParsedOrder>(
    orderId: string,
    field: K,
    value: ParsedOrder[K]
  ) => {
    setOrders((prev) =>
      prev.map((o) =>
        o.id === orderId ? updateBulkShippingRow(o, field, value, t) : o
      )
    );
  };

  // Toggle order selection
  const toggleOrderSelection = (orderId: string) => {
    setOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, selected: !o.selected } : o))
    );
  };

  // Toggle all orders selection
  const toggleAllOrders = (selected: boolean) => {
    setOrders((prev) =>
      prev.map((o) =>
        o.status === "ready" && o.selectedQuote ? { ...o, selected } : o
      )
    );
  };

  // Select a pricing option for an order
  const selectQuoteForOrder = (orderId: string, quote: PricingOption) => {
    const key = `${quote.carrier.id}__${quote.carrierService.code}`;
    setOrders((prev) =>
      prev.map((o) =>
        o.id === orderId ? { ...o, selectedQuote: quote, quoteId: key } : o
      )
    );
  };

  // Initialize package edits when expanding a row
  const initPackageEdit = (order: ParsedOrder) => {
    if (!packageEdits[order.id]) {
      setPackageEdits((prev) => ({
        ...prev,
        [order.id]: {
          weight: order.weight,
          weightUnit: order.weightUnit,
          length: order.length,
          width: order.width,
          height: order.height,
          dimensionUnit: order.dimensionUnit,
          deliveryNotes: order.deliveryNotes,
        },
      }));
    }
  };

  // Update a package edit field
  const updatePackageEdit = (
    orderId: string,
    field: string,
    value: number | string
  ) => {
    setPackageEdits((prev) => ({
      ...prev,
      [orderId]: {
        ...prev[orderId],
        [field]: value,
      },
    }));
  };

  // Check if package has been modified
  const hasPackageChanges = (order: ParsedOrder) => {
    const edits = packageEdits[order.id];
    if (!edits) return false;
    return (
      edits.weight !== order.weight ||
      edits.weightUnit !== order.weightUnit ||
      edits.length !== order.length ||
      edits.width !== order.width ||
      edits.height !== order.height ||
      edits.dimensionUnit !== order.dimensionUnit ||
      edits.deliveryNotes !== order.deliveryNotes
    );
  };

  // Cancel package changes
  const cancelPackageChanges = (orderId: string) => {
    const order = orders.find((o) => o.id === orderId);
    if (order) {
      setPackageEdits((prev) => ({
        ...prev,
        [orderId]: {
          weight: order.weight,
          weightUnit: order.weightUnit,
          length: order.length,
          width: order.width,
          height: order.height,
          dimensionUnit: order.dimensionUnit,
          deliveryNotes: order.deliveryNotes,
        },
      }));
    }
  };

  // Confirm package changes and re-fetch quote
  const confirmPackageChanges = (order: ParsedOrder) => {
    const edits = packageEdits[order.id];
    if (!edits) return;

    // Update the order with new values
    const updatedOrder = {
      ...order,
      weight: edits.weight,
      weightUnit: edits.weightUnit,
      length: edits.length,
      width: edits.width,
      height: edits.height,
      dimensionUnit: edits.dimensionUnit,
      deliveryNotes: edits.deliveryNotes,
    };

    setOrders((prev) =>
      prev.map((o) => (o.id === order.id ? updatedOrder : o))
    );

    // Re-fetch quote with new dimensions
    void refetchQuoteForOrder(updatedOrder);
  };

  // Re-fetch quote for a single order (after package changes)
  const refetchQuoteForOrder = async (order: ParsedOrder) => {
    if (!senderAddress || !order.validatedAddress) return;

    // Set status to fetching_quote
    setOrders((prev) =>
      prev.map((o) =>
        o.id === order.id
          ? {
              ...o,
              status: "fetching_quote",
              availableQuotes: undefined,
              selectedQuote: undefined,
              quoteId: undefined,
            }
          : o
      )
    );

    try {
      const response = await calculateQuote.mutateAsync({
        senderName: senderAddress.name,
        senderPhone: senderAddress.phone,
        senderEmail: "",
        shipFrom: {
          street1: senderAddress.street1,
          street2: senderAddress.street2,
          city: senderAddress.city,
          state: senderAddress.state,
          postalCode: senderAddress.postalCode,
          country: senderAddress.country,
        },
        recipientName: order.recipientName,
        recipientPhone: order.recipientPhone,
        recipientEmail: "",
        shipTo: order.validatedAddress,
        packages: [
          {
            weight: order.weight,
            weightUnit: order.weightUnit,
            length: order.length,
            width: order.width,
            height: order.height,
            dimensionUnit: order.dimensionUnit,
          },
        ],
        deliveryNotes: order.deliveryNotes,
      });

      if (response.data.options.length > 0) {
        const options = response.data.options;
        // Pricing-redesign: default to fastest (legacy preferred
        // LOCAL_FULL_SERVICE which no longer exists in option shape).
        const selectedQuote = options
          .slice()
          .sort(
            (a, b) =>
              (a.carrierService.estimatedDays ?? 999) -
              (b.carrierService.estimatedDays ?? 999)
          )[0];

        setOrders((prev) =>
          prev.map((o) =>
            o.id === order.id
              ? {
                  ...o,
                  status: "ready",
                  availableQuotes: options,
                  selectedQuote,
                  quoteId: `${selectedQuote.carrier.id}__${selectedQuote.carrierService.code}`,
                }
              : o
          )
        );
      }
    } catch (error) {
      console.error("Error re-fetching quote:", error);
      setOrders((prev) =>
        prev.map((o) =>
          o.id === order.id
            ? {
                ...o,
                status: "needs_fix",
                errorMessage:
                  error instanceof ShippingTextError
                    ? error.localizedMessage(t)
                    : t("bulkShip.pricingError"),
              }
            : o
        )
      );
    }
  };

  // Process current order (create shipment)
  const processCurrentOrder = async () => {
    if (currentOrderIndex === null) return;

    const order = orders[currentOrderIndex];
    if (order?.status !== "ready" || !order.selectedQuote || !senderAddress)
      return;

    const sender = shipmentContactInputSchema(
      senderAddress.country,
      `${t("validation.phoneRequired")}. ${t("validation.phoneInvalid")}`
    ).safeParse(senderAddress);
    const contactError = bulkShippingContactMessage(order, t);
    if (!sender.success || contactError) {
      const errorMessage =
        contactError ??
        `${t("orderDetails.sender")}: ${sender.success ? "" : bulkShippingContactError(sender.error.issues, senderAddress.name, "sender", t)}`;
      setOrders((current) =>
        current.map((row, index) =>
          index === currentOrderIndex
            ? { ...row, status: "needs_fix", errorMessage }
            : row
        )
      );
      toast({
        title: t("bulkShip.createErrorTitle"),
        description: errorMessage,
        variant: "destructive",
      });
      return;
    }
    const recipient = shipmentContactInputSchema(
      order.validatedAddress?.country ?? "CA"
    ).parse({
      name: order.recipientName,
      phone: order.recipientPhone,
      email: order.recipientEmail,
    });

    // Update status to processing
    setOrders((prev) =>
      prev.map((o, idx) =>
        idx === currentOrderIndex ? { ...o, status: "processing" } : o
      )
    );

    try {
      // ⚠️ NOT an "echo" any more — that word was the bug (#533). The order sends
      // the QUOTE ID and the server bills the row it wrote. `labelPriceCents` is
      // display-only; echoing it back is what let a client set its own price.
      // Bulk-ship doesn't expose pickup add-on toggles per row yet (TODO).
      const opt = order.selectedQuote;
      const selectedRate = {
        quoteId: opt.quoteId,
        carrierId: opt.carrier.id,
        carrierServiceCode: opt.carrierService.code,
        fromServiceAreaId: opt.fromServiceAreaId,
        toServiceAreaId: opt.toServiceAreaId,
        currency: opt.currency,
      };

      // Phase 10a.5: bulk-ship always uses an AddressBook entry as the
      // sender (selector at top of form). Forward the entry ID so the server
      // reuses its Address row (enables PickupBooking dedup).
      const senderEntryId = selectedAddressId ?? defaultContact?.id;
      const shipment = {
        senderName: sender.data.name,
        senderPhone: sender.data.phone,
        senderEmail: sender.data.email,
        ...(senderEntryId
          ? { shipFromAddressBookEntryId: senderEntryId }
          : {
              shipFrom: {
                street1: senderAddress.street1,
                street2: senderAddress.street2 || undefined,
                city: senderAddress.city,
                state: senderAddress.state,
                postalCode: senderAddress.postalCode,
                country: senderAddress.country,
                latitude: senderAddress.latitude,
                longitude: senderAddress.longitude,
              },
            }),
        recipientName: recipient.name,
        recipientPhone: recipient.phone,
        recipientEmail: recipient.email,
        shipTo: order.validatedAddress!,
        packages: [
          {
            // The row's OWN units. A spreadsheet may mix them row to row, and
            // the server converts each one — see the unify-unit-conversion PRD.
            weight: order.weight,
            weightUnit: order.weightUnit,
            length: order.length,
            width: order.width,
            height: order.height,
            dimensionUnit: order.dimensionUnit,
          },
        ],
        deliveryNotes: order.deliveryNotes || undefined,
        selectedRate,
        // Per-row pickup add-on. The server runs find-or-attach via the unique
        // constraint on (pickupAddressId, scheduledDate, slotId, pickupServiceId)
        // — rows with matching keys consolidate into one PickupBooking.
        // Bulk MVP: auto-pick the first tier per row. A per-row picker is a
        // future enhancement — most bulk users want consistent pickup tiers.
        ...(order.pickup && opt.pickupOptions.length > 0
          ? {
              pickup: {
                pickupServiceId: opt.pickupOptions[0].pickupServiceId,
                scheduledDate: order.pickup.date,
                slotId: order.pickup.slotId,
                pickupPriceCents: opt.pickupOptions[0].pickupPriceCents,
              },
            }
          : {}),
      };

      // Build request based on payment choice
      // paymentMethodId presence implies charge card
      const request = (() => {
        if (paymentChoice === "SAVED_CARD" && selectedPaymentMethod) {
          return {
            paymentMethodType: PaymentMethodType.SAVED_CARD,
            paymentMethodId: selectedPaymentMethod.id,
            shipments: [shipment] as [typeof shipment],
          };
        } else if (paymentChoice === "INVOICE" && effectivePaymentTermsId) {
          return {
            paymentMethodType: PaymentMethodType.INVOICE,
            paymentTermsId: effectivePaymentTermsId,
            shipments: [shipment] as [typeof shipment],
          };
        } else {
          return {
            paymentMethodType: PaymentMethodType.PAYMENT_LINK,
            shipments: [shipment] as [typeof shipment],
          };
        }
      })();

      const createdOrder = await createOrder.mutateAsync(request);

      // Update order as completed
      setOrders((prev) =>
        prev.map((o, idx) =>
          idx === currentOrderIndex
            ? { ...o, status: "completed", createdOrder }
            : o
        )
      );

      // Auto-advance to next ready order
      advanceToNextOrder();
    } catch (error: unknown) {
      console.error("Error creating order:", error);
      const message = axios.isAxiosError<{ message?: string }>(error)
        ? error.response?.data?.message
        : undefined;
      setOrders((prev) =>
        prev.map((o, idx) =>
          idx === currentOrderIndex
            ? {
                ...o,
                status: "error",
                errorMessage: message ?? t("bulkShip.createErrorBody"),
              }
            : o
        )
      );
      toast({
        title: t("bulkShip.createErrorTitle"),
        description: message ?? t("bulkShip.createErrorBody"),
        variant: "destructive",
      });
    }
  };

  // Advance to next ready order
  const advanceToNextOrder = () => {
    setOrders((currentOrders) => {
      const nextIndex = currentOrders.findIndex(
        (o, idx) =>
          idx > (currentOrderIndex ?? -1) &&
          o.status === "ready" &&
          o.selectedQuote &&
          o.selected
      );

      if (nextIndex !== -1) {
        setCurrentOrderIndex(nextIndex);
      } else {
        // Check if all selected orders are done
        const selectedOrders = currentOrders.filter((o) => o.selected);
        const allSelectedDone = selectedOrders.every(
          (o) =>
            o.status === "completed" ||
            o.status === "skipped" ||
            o.status === "error" ||
            o.status === "needs_fix"
        );
        if (allSelectedDone) {
          setPhase("complete");
        } else {
          setCurrentOrderIndex(null);
        }
      }
      return currentOrders;
    });
  };

  // Skip current order
  const skipCurrentOrder = () => {
    if (currentOrderIndex === null) return;

    setOrders((prev) =>
      prev.map((o, idx) =>
        idx === currentOrderIndex ? { ...o, status: "skipped" } : o
      )
    );

    advanceToNextOrder();
  };

  // Start processing from first ready order
  const startProcessing = () => {
    const firstReadyIndex = orders.findIndex(
      (o) => o.status === "ready" && o.selectedQuote && o.selected
    );
    if (firstReadyIndex !== -1) {
      setCurrentOrderIndex(firstReadyIndex);
    }
  };

  // Tax-inclusive per-order charge: label + label tax + (cheapest pickup tier
  // + its tax, when the row opted into pickup). Bulk view shows the cheapest
  // tier in row summaries since there's no per-row tier picker.
  const orderChargeCents = (order: ParsedOrder): number => {
    const opt = order.selectedQuote;
    if (!opt) return 0;
    const labelCharge = opt.labelPriceCents + (opt.taxCents ?? 0);
    if (!order.pickup || opt.pickupOptions.length === 0) {
      return labelCharge;
    }
    const cheapest = opt.pickupOptions.reduce((m, t) =>
      t.pickupPriceCents < m.pickupPriceCents ? t : m
    );
    return labelCharge + cheapest.pickupPriceCents + (cheapest.taxCents ?? 0);
  };

  // Format currency
  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "CAD",
    }).format(cents / 100);
  };

  // Render a pickup choice as "Wed May 7 · Morning (09:00–12:00)"
  const formatPickupLabel = (
    pickup: { date: string; slotId: string },
    config: BulkPickupConfig | null
  ): string => {
    const slot = config?.timeSlots.find((s) => s.id === pickup.slotId);
    const dateLabel = pickup.date
      ? new Date(pickup.date).toLocaleDateString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric",
        })
      : "—";
    if (!slot) return dateLabel;
    return `${dateLabel} · ${slot.name}`;
  };

  // Calculate total cost. Includes pickup add-on per row when set.
  const totalCost = orders
    .filter(
      (o) =>
        (o.status === "ready" || o.status === "completed") &&
        o.selectedQuote &&
        o.selected
    )
    .reduce((sum, o) => sum + orderChargeCents(o), 0);

  // Render upload phase
  if (phase === "upload") {
    return (
      <div className="flex flex-col flex-1">
        <div className="px-6 pt-6 pb-4 border-b">
          <h2 className="text-xl font-semibold">{t("bulkShip.uploadTitle")}</h2>
          <p className="text-sm text-default-500 mt-1">
            {t("bulkShip.uploadBody")}
          </p>
        </div>

        <div className="flex-1 overflow-auto px-6 py-8">
          <div className="max-w-2xl mx-auto">
            <div className="flex justify-center mb-6">
              <Button
                variant="bordered"
                onClick={handleDownloadTemplate}
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                {t("bulkShip.downloadTemplate")}
              </Button>
            </div>

            <div
              className={`relative p-8 rounded-lg border-2 border-dashed transition-colors ${
                isDragging
                  ? "border-primary bg-primary/5"
                  : isInvalid
                    ? "border-destructive"
                    : "border-muted"
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center justify-center py-8">
                <FileSpreadsheet className="h-12 w-12 text-default-500 mb-4" />
                <p className="text-default-500 text-center mb-4">
                  {t("bulkShip.dropSpreadsheet")}{" "}
                  <label className="text-primary cursor-pointer hover:underline">
                    {t("bulkShip.browse")}
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      className="hidden"
                      onChange={handleFileInput}
                    />
                  </label>
                </p>
                <p className="text-xs text-default-500">
                  {t("bulkShip.supportedFiles")}
                </p>
              </div>

              {isInvalid && (
                <p className="text-sm text-danger text-center mt-2">
                  {t("bulkShip.invalidFile")}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render complete phase
  if (phase === "complete") {
    const completedOrders = orders.filter((o) => o.status === "completed");
    const totalPaid = completedOrders.reduce(
      (sum, o) => sum + orderChargeCents(o),
      0
    );

    return (
      <div className="flex flex-col flex-1">
        <div className="flex-1 overflow-auto px-6 py-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-700 mb-2">
              {t("bulkShip.ordersCreated", { count: completedOrders.length })}
            </h2>
            <p className="text-default-500 mb-6">
              {t("bulkShip.total", { amount: formatCurrency(totalPaid) })}
            </p>

            <Card className="text-left mb-6">
              <CardBody className="pt-6">
                <div className="space-y-3">
                  <BulkShipmentResults
                    results={orders.map((order) => ({
                      id: order.id,
                      status: order.status,
                      recipientName: order.recipientName,
                      destination: [
                        order.validatedAddress?.city,
                        order.validatedAddress?.state,
                      ]
                        .filter(Boolean)
                        .join(", "),
                      charge: formatCurrency(orderChargeCents(order)),
                      order: order.createdOrder,
                    }))}
                  />
                </div>
              </CardBody>
            </Card>

            <div className="flex gap-3 justify-center">
              <Button variant="bordered" onClick={onClose}>
                {t("bulkShip.done")}
              </Button>
              <Button onClick={() => window.open("/orders", "_blank")}>
                {t("bulkShip.viewOrders")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render review phase (main table view)
  const currentOrder =
    currentOrderIndex !== null ? orders[currentOrderIndex] : null;
  const isLoading =
    isValidatingAll || isFetchingQuotes || fetchingQuoteCount > 0;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 border-b shrink-0">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">{t("bulkShip.reviewTitle")}</h2>
          <div className="text-center">
            <p className="text-2xl font-bold">
              {completedCount}/{orders.length}
            </p>
            <p className="text-xs text-default-500">
              {t("bulkShip.completed")}
            </p>
          </div>
        </div>

        {/* Ship From selector */}
        <div className="flex items-center gap-3">
          <MapPin className="h-4 w-4 text-default-500 shrink-0" />
          <span className="text-sm text-default-500 shrink-0">
            {t("bulkShip.shipFrom")}
          </span>
          {contacts.length > 0 ? (
            <Select
              size="sm"
              aria-label={t("bulkShip.shipFromAddress")}
              placeholder={t("bulkShip.selectAddress")}
              className="w-full max-w-sm"
              selectedKeys={
                selectedAddressId
                  ? [selectedAddressId]
                  : defaultContact?.id
                    ? [defaultContact.id]
                    : []
              }
              onSelectionChange={(keys) => {
                const first = Array.from(keys as Set<string>)[0];
                if (first) setSelectedAddressId(first);
              }}
            >
              {contacts.map((contact) => (
                <SelectItem key={contact.id}>
                  {`${contact.contactName} - ${contact.addressCity}, ${contact.addressProvince}`}
                </SelectItem>
              ))}
            </Select>
          ) : isLoadingContacts ? (
            <div className="flex items-center gap-2 text-sm text-default-500">
              <Spinner size="sm" />
              {t("bulkShip.loadingAddresses")}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm text-amber-600">
                {t("bulkShip.noSavedAddresses")}
              </span>
              <Button
                variant="light"
                size="sm"
                className="h-auto p-0 text-primary"
                onClick={() => window.open("/address-book", "_blank")}
              >
                {t("bulkShip.addAddressBook")}
              </Button>
            </div>
          )}
        </div>

        {/* Pickup picker */}
        <div className="flex items-start gap-3 mt-3">
          <Truck className="h-4 w-4 text-default-500 shrink-0 mt-1" />
          <div className="flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="text-sm text-default-500">
                {t("bulkShip.pickup")}
              </span>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="bulkPickupMode"
                  checked={pickupMode === "none"}
                  onChange={() => setPickupMode("none")}
                />
                {t("bulkShip.noPickup")}
              </label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="bulkPickupMode"
                  checked={pickupMode === "same"}
                  onChange={() => setPickupMode("same")}
                  disabled={!pickupConfig}
                />
                {t("bulkShip.samePickup")}
              </label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="bulkPickupMode"
                  checked={pickupMode === "custom"}
                  onChange={() => setPickupMode("custom")}
                  disabled={!pickupConfig}
                />
                {t("bulkShip.customPerRow")}
              </label>
            </div>

            {pickupMode === "same" && pickupConfig && (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={defaultPickup?.date ?? ""}
                  onChange={(e) =>
                    setDefaultPickup({
                      date: e.target.value,
                      slotId: defaultPickup?.slotId ?? "",
                    })
                  }
                  min={new Date().toISOString().split("T")[0]}
                  className="w-44 h-8 text-sm"
                />
                <Select
                  size="sm"
                  aria-label={t("bulkShip.timeSlot")}
                  placeholder={t("bulkShip.timeSlot")}
                  className="w-48"
                  selectedKeys={
                    defaultPickup?.slotId ? [defaultPickup.slotId] : []
                  }
                  onSelectionChange={(keys) => {
                    const v = Array.from(keys as Set<string>)[0];
                    if (v)
                      setDefaultPickup({
                        date: defaultPickup?.date ?? "",
                        slotId: v,
                      });
                  }}
                >
                  {pickupConfig.timeSlots.map((s) => (
                    <SelectItem key={s.id}>
                      {`${s.name} (${s.startTime}–${s.endTime})`}
                    </SelectItem>
                  ))}
                </Select>
              </div>
            )}

            {pickupMode === "custom" && (
              <p className="text-xs text-default-500">
                {t("bulkShip.customPickupHelp", {
                  edit: t("bulkShip.edit"),
                })}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Order Table */}
      <div className="flex-1 overflow-auto min-h-0">
        <div className="px-6 py-4">
          {/* Status summary */}
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-default-500 mb-4">
              <Spinner size="sm" />
              {isValidatingAll
                ? t("bulkShip.validating")
                : t("bulkShip.gettingPrices")}
            </div>
          )}

          {!isLoading && needsFixCount > 0 && (
            <div className="flex items-center gap-2 text-sm text-amber-600 mb-4">
              <AlertCircle className="h-4 w-4" />
              {t("bulkShip.needsAttention", { count: needsFixCount })}
            </div>
          )}

          <p className="text-xs text-default-500 mb-3">
            {t("bulkShip.bulkLimitNote")}
          </p>

          {/* Table */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr className="text-sm text-default-500">
                  <th className="p-3 w-10">
                    <Checkbox
                      isSelected={allSelected}
                      onValueChange={(checked: boolean) =>
                        toggleAllOrders(!!checked)
                      }
                      isDisabled={allSelectableOrders.length === 0}
                    />
                  </th>
                  <th className="text-left p-3">
                    {t("bulkShip.columns.recipient")}
                  </th>
                  <th className="text-left p-3">
                    {t("bulkShip.columns.destination")}
                  </th>
                  <th className="text-left p-3">
                    {t("bulkShip.columns.package")}
                  </th>
                  <th className="text-left p-3">
                    {t("bulkShip.columns.pickup")}
                  </th>
                  <th className="text-right p-3">
                    {t("bulkShip.columns.price")}
                  </th>
                  <th className="w-10 p-3"></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, idx) => (
                  <Fragment key={order.id}>
                    <tr
                      className={`border-t transition-colors cursor-pointer hover:bg-muted/30 ${
                        currentOrderIndex === idx
                          ? "bg-primary/5"
                          : order.status === "completed"
                            ? "bg-green-50"
                            : order.status === "needs_fix"
                              ? "bg-amber-50"
                              : ""
                      }`}
                      onClick={() => {
                        if (expandedOrderId !== order.id) {
                          initPackageEdit(order);
                        }
                        setExpandedOrderId(
                          expandedOrderId === order.id ? null : order.id
                        );
                      }}
                    >
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        {order.status === "pending" ||
                        order.status === "validating" ||
                        order.status === "fetching_quote" ? (
                          <Spinner size="sm" />
                        ) : order.status === "ready" && order.selectedQuote ? (
                          <Checkbox
                            isSelected={order.selected}
                            onValueChange={() => toggleOrderSelection(order.id)}
                          />
                        ) : order.status === "ready" && !order.selectedQuote ? (
                          <Spinner size="sm" />
                        ) : order.status === "processing" ? (
                          <Spinner size="sm" color="primary" />
                        ) : order.status === "completed" ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : order.status === "needs_fix" ? (
                          <AlertCircle className="h-4 w-4 text-amber-600" />
                        ) : order.status === "skipped" ? (
                          <X className="h-4 w-4 text-default-500" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-danger" />
                        )}
                      </td>
                      <td className="p-3">
                        <p className="font-medium">{order.recipientName}</p>
                        {order.recipientPhone && (
                          <p className="text-sm text-default-500">
                            {order.recipientPhone}
                          </p>
                        )}
                      </td>
                      <td className="p-3">
                        {order.status === "needs_fix" ? (
                          <span className="text-amber-600">
                            {order.errorMessage ?? t("bulkShip.invalidAddress")}
                          </span>
                        ) : (
                          <span>
                            {order.validatedAddress?.city ??
                              order.recipientCity}
                            ,{" "}
                            {order.validatedAddress?.state ??
                              order.recipientProvince}
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-sm text-default-500">
                        {order.weight}
                        {order.weightUnit}
                      </td>
                      <td
                        className="p-3 text-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center gap-2">
                          {order.pickup ? (
                            <span className="text-foreground">
                              {formatPickupLabel(order.pickup, pickupConfig)}
                            </span>
                          ) : (
                            <span className="text-default-500">
                              {t("bulkShip.dropoff")}
                            </span>
                          )}
                          {pickupConfig &&
                            (order.selectedQuote?.pickupOptions.length ?? 0) >
                              0 && (
                              <Button
                                variant="light"
                                size="sm"
                                className="h-6 px-2 text-xs"
                                onClick={() =>
                                  setEditPickupForOrderId(order.id)
                                }
                              >
                                {t("bulkShip.edit")}
                              </Button>
                            )}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        {order.selectedQuote ? (
                          <div className="flex items-center justify-end gap-2">
                            {order.pickup &&
                            order.selectedQuote.pickupOptions.length > 0 ? (
                              <Zap className="h-4 w-4 text-primary" />
                            ) : (
                              <Truck className="h-4 w-4 text-default-500" />
                            )}
                            <span className="font-medium">
                              {/* Bulk: row summary uses cheapest tier. */}
                              {formatCurrency(orderChargeCents(order))}
                            </span>
                          </div>
                        ) : order.status === "fetching_quote" ||
                          order.status === "ready" ? (
                          <Spinner size="sm" className="ml-auto" />
                        ) : (
                          <span className="text-default-500">—</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {expandedOrderId === order.id ? (
                          <ChevronUp className="h-4 w-4 text-default-500" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-default-500" />
                        )}
                      </td>
                    </tr>

                    {/* Expanded row for editing */}
                    {expandedOrderId === order.id && (
                      <tr
                        key={`${order.id}-expanded`}
                        className="border-t bg-muted/20"
                      >
                        <td colSpan={7} className="p-4">
                          <div className="grid grid-cols-3 gap-6">
                            {/* Column 1: Recipient Info */}
                            <div className="space-y-3">
                              <h4 className="text-xs font-semibold text-default-500 uppercase">
                                {t("bulkShip.columns.recipient")}
                              </h4>
                              <div>
                                <label className="text-xs font-medium text-default-700">
                                  {t("bulkShip.name")}
                                </label>
                                <Input
                                  value={order.recipientName}
                                  onChange={(e) =>
                                    updateOrderField(
                                      order.id,
                                      "recipientName",
                                      e.target.value
                                    )
                                  }
                                  className="h-8"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-medium text-default-700">
                                  {t("bulkShip.phone")}
                                </label>
                                <Input
                                  value={order.recipientPhone}
                                  onChange={(e) =>
                                    updateOrderField(
                                      order.id,
                                      "recipientPhone",
                                      e.target.value
                                    )
                                  }
                                  placeholder={t("bulkShip.enterPhone")}
                                  className="h-8"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-medium text-default-700">
                                  {t("bulkShip.email")}
                                </label>
                                <Input
                                  value={order.recipientEmail}
                                  onChange={(e) =>
                                    updateOrderField(
                                      order.id,
                                      "recipientEmail",
                                      e.target.value
                                    )
                                  }
                                  placeholder={t("bulkShip.enterEmail")}
                                  className="h-8"
                                />
                              </div>
                              <div className="flex items-center justify-between">
                                <label className="text-xs font-medium text-default-700">
                                  {t("bulkShip.streetAddress")}
                                </label>
                                {order.validatedAddress && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium">
                                    {t("bulkShip.verifiedByGoogle")}
                                  </span>
                                )}
                              </div>
                              <Input
                                value={
                                  order.validatedAddress?.street1 ??
                                  order.recipientAddress
                                }
                                aria-label={t("bulkShip.streetAddress")}
                                onChange={(e) =>
                                  updateOrderField(
                                    order.id,
                                    "recipientAddress",
                                    e.target.value
                                  )
                                }
                                isDisabled={
                                  order.status === "processing" ||
                                  order.status === "completed"
                                }
                                className="h-8"
                              />
                              <div>
                                <label className="text-xs font-medium text-default-700">
                                  {t("bulkShip.unit")}
                                </label>
                                <Input
                                  value={order.recipientUnit}
                                  onChange={(e) =>
                                    updateOrderField(
                                      order.id,
                                      "recipientUnit",
                                      e.target.value
                                    )
                                  }
                                  placeholder={t("bulkShip.optional")}
                                  className="h-8"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="text-xs font-medium text-default-700">
                                    {t("bulkShip.city")}
                                  </label>
                                  <Input
                                    value={
                                      order.validatedAddress?.city ??
                                      order.recipientCity
                                    }
                                    aria-label={t("bulkShip.city")}
                                    onChange={(e) =>
                                      updateOrderField(
                                        order.id,
                                        "recipientCity",
                                        e.target.value
                                      )
                                    }
                                    isDisabled={
                                      order.status === "processing" ||
                                      order.status === "completed"
                                    }
                                    className="h-8"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-default-700">
                                    {t("bulkShip.province")}
                                  </label>
                                  <Input
                                    value={
                                      order.validatedAddress?.state ??
                                      order.recipientProvince
                                    }
                                    readOnly
                                    className="h-8 bg-muted/50"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="text-xs font-medium text-default-700">
                                  {t("bulkShip.postalCode")}
                                </label>
                                <Input
                                  value={
                                    order.validatedAddress?.postalCode ??
                                    order.recipientPostalCode
                                  }
                                  readOnly
                                  className="h-8 bg-muted/50"
                                />
                              </div>
                            </div>

                            {/* Column 2: Package & Notes */}
                            <div className="space-y-3">
                              <h4 className="text-xs font-semibold text-default-500 uppercase">
                                {t("bulkShip.columns.package")}
                              </h4>
                              {(() => {
                                const edits = packageEdits[order.id] || {
                                  weight: order.weight,
                                  weightUnit: order.weightUnit,
                                  length: order.length,
                                  width: order.width,
                                  height: order.height,
                                  dimensionUnit: order.dimensionUnit,
                                  deliveryNotes: order.deliveryNotes,
                                };
                                const isWeightChanged =
                                  edits.weight !== order.weight ||
                                  edits.weightUnit !== order.weightUnit;
                                const isLengthChanged =
                                  edits.length !== order.length;
                                const isWidthChanged =
                                  edits.width !== order.width;
                                const isHeightChanged =
                                  edits.height !== order.height;
                                const isDimensionUnitChanged =
                                  edits.dimensionUnit !== order.dimensionUnit;
                                const isNotesChanged =
                                  edits.deliveryNotes !== order.deliveryNotes;

                                return (
                                  <>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <label
                                          className={`text-xs font-medium text-default-700 ${isWeightChanged ? "text-amber-600" : ""}`}
                                        >
                                          {t("bulkShip.weight")}{" "}
                                          {isWeightChanged && "*"}
                                        </label>
                                        <div className="flex gap-1">
                                          <Input
                                            type="number"
                                            value={String(edits.weight ?? "")}
                                            onChange={(e) =>
                                              updatePackageEdit(
                                                order.id,
                                                "weight",
                                                Number(e.target.value)
                                              )
                                            }
                                            className={`flex-1 ${isWeightChanged ? "border-amber-400 bg-amber-50" : ""}`}
                                          />
                                          <Select
                                            size="sm"
                                            aria-label={t(
                                              "bulkShip.weightUnit"
                                            )}
                                            className={`w-16 ${isWeightChanged ? "border-amber-400 bg-amber-50" : ""}`}
                                            selectedKeys={[edits.weightUnit]}
                                            onSelectionChange={(keys) => {
                                              const v = Array.from(
                                                keys as Set<string>
                                              )[0];
                                              if (v)
                                                updatePackageEdit(
                                                  order.id,
                                                  "weightUnit",
                                                  v
                                                );
                                            }}
                                          >
                                            <SelectItem key="kg">kg</SelectItem>
                                            <SelectItem key="lb">lb</SelectItem>
                                            <SelectItem key="oz">oz</SelectItem>
                                            <SelectItem key="g">g</SelectItem>
                                          </Select>
                                        </div>
                                      </div>
                                      <div>
                                        <label
                                          className={`text-xs font-medium text-default-700 ${isDimensionUnitChanged ? "text-amber-600" : ""}`}
                                        >
                                          {t("bulkShip.dimensionUnit")}{" "}
                                          {isDimensionUnitChanged && "*"}
                                        </label>
                                        <Select
                                          size="sm"
                                          aria-label={t(
                                            "bulkShip.dimensionUnit"
                                          )}
                                          className={
                                            isDimensionUnitChanged
                                              ? "border-amber-400 bg-amber-50"
                                              : ""
                                          }
                                          selectedKeys={[edits.dimensionUnit]}
                                          onSelectionChange={(keys) => {
                                            const v = Array.from(
                                              keys as Set<string>
                                            )[0];
                                            if (v)
                                              updatePackageEdit(
                                                order.id,
                                                "dimensionUnit",
                                                v
                                              );
                                          }}
                                        >
                                          <SelectItem key="cm">cm</SelectItem>
                                          <SelectItem key="in">in</SelectItem>
                                        </Select>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                      <div>
                                        <label
                                          className={`text-xs font-medium text-default-700 ${isLengthChanged ? "text-amber-600" : ""}`}
                                        >
                                          {t("bulkShip.length")} (
                                          {edits.dimensionUnit}){" "}
                                          {isLengthChanged && "*"}
                                        </label>
                                        <Input
                                          type="number"
                                          value={String(edits.length ?? "")}
                                          onChange={(e) =>
                                            updatePackageEdit(
                                              order.id,
                                              "length",
                                              Number(e.target.value)
                                            )
                                          }
                                          className={
                                            isLengthChanged
                                              ? "border-amber-400 bg-amber-50"
                                              : ""
                                          }
                                        />
                                      </div>
                                      <div>
                                        <label
                                          className={`text-xs font-medium text-default-700 ${isWidthChanged ? "text-amber-600" : ""}`}
                                        >
                                          {t("bulkShip.width")} (
                                          {edits.dimensionUnit}){" "}
                                          {isWidthChanged && "*"}
                                        </label>
                                        <Input
                                          type="number"
                                          value={String(edits.width ?? "")}
                                          onChange={(e) =>
                                            updatePackageEdit(
                                              order.id,
                                              "width",
                                              Number(e.target.value)
                                            )
                                          }
                                          className={
                                            isWidthChanged
                                              ? "border-amber-400 bg-amber-50"
                                              : ""
                                          }
                                        />
                                      </div>
                                      <div>
                                        <label
                                          className={`text-xs font-medium text-default-700 ${isHeightChanged ? "text-amber-600" : ""}`}
                                        >
                                          {t("bulkShip.height")} (
                                          {edits.dimensionUnit}){" "}
                                          {isHeightChanged && "*"}
                                        </label>
                                        <Input
                                          type="number"
                                          value={String(edits.height ?? "")}
                                          onChange={(e) =>
                                            updatePackageEdit(
                                              order.id,
                                              "height",
                                              Number(e.target.value)
                                            )
                                          }
                                          className={
                                            isHeightChanged
                                              ? "border-amber-400 bg-amber-50"
                                              : ""
                                          }
                                        />
                                      </div>
                                    </div>
                                    <div>
                                      <label
                                        className={`text-xs font-medium text-default-700 ${isNotesChanged ? "text-amber-600" : ""}`}
                                      >
                                        {t("bulkShip.deliveryNotes")}{" "}
                                        {isNotesChanged && "*"}
                                      </label>
                                      <Input
                                        value={edits.deliveryNotes}
                                        onChange={(e) =>
                                          updatePackageEdit(
                                            order.id,
                                            "deliveryNotes",
                                            e.target.value
                                          )
                                        }
                                        className={`h-8 ${isNotesChanged ? "border-amber-400 bg-amber-50" : ""}`}
                                      />
                                    </div>
                                  </>
                                );
                              })()}

                              {order.status === "needs_fix" ? (
                                <Button
                                  size="sm"
                                  variant="bordered"
                                  className="mt-3"
                                  onClick={() => {
                                    setOrders((prev) =>
                                      prev.map((o) =>
                                        o.id === order.id
                                          ? {
                                              ...o,
                                              status: "pending",
                                              errorMessage: undefined,
                                            }
                                          : o
                                      )
                                    );
                                    void validateAllAddresses([order]);
                                  }}
                                >
                                  {t("bulkShip.retryValidation")}
                                </Button>
                              ) : order.validatedAddress &&
                                order.status === "ready" &&
                                hasPackageChanges(order) ? (
                                <div className="flex gap-2 mt-3">
                                  <Button
                                    size="sm"
                                    variant="bordered"
                                    onClick={() =>
                                      cancelPackageChanges(order.id)
                                    }
                                  >
                                    {t("actions.cancel")}
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => confirmPackageChanges(order)}
                                  >
                                    {t("bulkShip.confirmUpdatePrice")}
                                  </Button>
                                </div>
                              ) : null}
                            </div>

                            {/* Column 3: Shipping Options */}
                            <div className="space-y-3">
                              <h4 className="text-xs font-semibold text-default-500 uppercase">
                                {t("bulkShip.shippingOption")}
                              </h4>
                              {order.availableQuotes &&
                              order.availableQuotes.length > 0 ? (
                                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                                  {order.availableQuotes.map((quote) => {
                                    // Pricing-redesign: pickup is now a
                                    // separate add-on (bulk-ship doesn't
                                    // expose the toggle yet — TODO). Show
                                    // "Pickup avail" badge for options that
                                    // support it; otherwise plain.
                                    const pickupAvailable =
                                      quote.pickupOptions.length > 0;
                                    // Bulk: option-list uses cheapest tier;
                                    // shown tax-inclusive to match what the
                                    // customer is actually charged.
                                    const cheapestTier = pickupAvailable
                                      ? quote.pickupOptions.reduce((m, t) =>
                                          t.pickupPriceCents <
                                          m.pickupPriceCents
                                            ? t
                                            : m
                                        )
                                      : null;
                                    const pickupMinCharge = cheapestTier
                                      ? cheapestTier.pickupPriceCents +
                                        (cheapestTier.taxCents ?? 0)
                                      : 0;
                                    const labelCharge =
                                      quote.labelPriceCents +
                                      (quote.taxCents ?? 0);
                                    const key = `${quote.carrier.id}__${quote.carrierService.code}`;
                                    const selectedKey = order.selectedQuote
                                      ? `${order.selectedQuote.carrier.id}__${order.selectedQuote.carrierService.code}`
                                      : null;
                                    return (
                                      <div
                                        key={key}
                                        className={`p-2 border rounded-lg cursor-pointer transition-colors ${
                                          selectedKey === key
                                            ? "border-primary bg-primary/5"
                                            : "border-gray-200 hover:border-gray-300"
                                        }`}
                                        onClick={() =>
                                          selectQuoteForOrder(order.id, quote)
                                        }
                                      >
                                        <div className="flex items-center justify-between mb-1">
                                          <div className="flex items-center gap-2">
                                            <Truck className="h-4 w-4 text-default-500 shrink-0" />
                                            <p className="font-medium text-xs">
                                              {quote.carrier.name} —{" "}
                                              {quote.carrierService.name}
                                            </p>
                                          </div>
                                          <p className="font-bold text-sm">
                                            {formatCurrency(labelCharge)}
                                          </p>
                                        </div>
                                        <div className="flex items-center justify-between text-xs text-default-500 pl-6">
                                          <span>
                                            {quote.carrierService
                                              .estimatedDays != null
                                              ? t("bulkShip.businessDays", {
                                                  count:
                                                    quote.carrierService
                                                      .estimatedDays ?? 0,
                                                })
                                              : t("bulkShip.sameDay")}
                                          </span>
                                          {pickupAvailable && (
                                            <span className="flex items-center gap-1 text-green-600">
                                              <Zap className="h-3 w-3" />
                                              {quote.pickupOptions.length > 1
                                                ? t(
                                                    "bulkShip.pickupAvailableFrom",
                                                    {
                                                      amount:
                                                        formatCurrency(
                                                          pickupMinCharge
                                                        ),
                                                    }
                                                  )
                                                : t(
                                                    "bulkShip.pickupAvailable",
                                                    {
                                                      amount:
                                                        formatCurrency(
                                                          pickupMinCharge
                                                        ),
                                                    }
                                                  )}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : order.status === "fetching_quote" ? (
                                <div className="flex items-center gap-2 text-sm text-default-500">
                                  <Spinner size="sm" />
                                  {t("bulkShip.loadingOptions")}
                                </div>
                              ) : (
                                <p className="text-sm text-default-500">
                                  {t("bulkShip.noOptions")}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bottom Panel - Payment & Actions */}
      <div className="border-t bg-background shrink-0 rounded-b-lg">
        {currentOrder ? (
          // Processing a specific order
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="font-medium">
                  {t("bulkShip.orderLabel", {
                    number: currentOrderIndex! + 1,
                    name: currentOrder.recipientName,
                  })}
                </p>
                <p className="text-sm text-default-500">
                  {currentOrder.validatedAddress?.city},{" "}
                  {currentOrder.validatedAddress?.state} •{" "}
                  {currentOrder.selectedQuote?.carrierService.name}
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold">
                  {formatCurrency(orderChargeCents(currentOrder))}
                </p>
                <p className="text-sm text-default-500">
                  {paymentChoice === "SAVED_CARD" && selectedPaymentMethod
                    ? t("bulkShip.payWithCard", {
                        last4: selectedPaymentMethod.last4,
                      })
                    : paymentChoice === "INVOICE"
                      ? t("bulkShip.invoiceNetTerms")
                      : t("bulkShip.decideLater")}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Button variant="bordered" onClick={skipCurrentOrder}>
                {t("bulkShip.skip")}
              </Button>
              <Button
                onClick={processCurrentOrder}
                disabled={createOrder.isPending}
                className="min-w-[150px]"
              >
                {createOrder.isPending ? (
                  <Spinner size="sm" />
                ) : paymentChoice === "SAVED_CARD" && selectedPaymentMethod ? (
                  t("bulkShip.payAndCreate")
                ) : (
                  t("bulkShip.createOrder")
                )}
              </Button>
            </div>
          </div>
        ) : (
          // Ready to start or all done
          <div className="px-6 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <p className="font-medium">
                  {t("bulkShip.readySummary", {
                    count: readyCount,
                    total: formatCurrency(totalCost),
                  })}
                </p>
                {needsFixCount > 0 && (
                  <p className="text-sm text-amber-600">
                    {t("bulkShip.needFixes", { count: needsFixCount })}
                  </p>
                )}
              </div>

              {/* Payment Method Selector */}
              <Select
                aria-label={t("bulkShip.paymentMethod")}
                className="w-[220px]"
                startContent={
                  paymentChoice === "INVOICE" ? (
                    <FileText className="h-4 w-4 text-default-500 shrink-0" />
                  ) : (
                    <CreditCard className="h-4 w-4 text-default-500 shrink-0" />
                  )
                }
                selectedKeys={[
                  paymentChoice === "SAVED_CARD" && selectedPaymentMethodId
                    ? `card:${selectedPaymentMethodId}`
                    : paymentChoice === "INVOICE"
                      ? "invoice"
                      : "decide-later",
                ]}
                onSelectionChange={(keys) => {
                  const value = Array.from(keys as Set<string>)[0];
                  if (!value) return;
                  if (value === "decide-later") {
                    setPaymentChoice("DECIDE_LATER");
                    setSelectedPaymentMethodId(null);
                  } else if (value === "invoice") {
                    setPaymentChoice("INVOICE");
                    setSelectedPaymentMethodId(null);
                  } else if (value.startsWith("card:")) {
                    setPaymentChoice("SAVED_CARD");
                    setSelectedPaymentMethodId(value.replace("card:", ""));
                  }
                }}
                disabledKeys={
                  hasNetTermsAccess && hasUnpaidInvoices
                    ? ["invoice-disabled"]
                    : []
                }
              >
                <>
                  {paymentMethods.map((pm) => (
                    <SelectItem key={`card:${pm.id}`}>
                      {pm.isDefault
                        ? t("bulkShip.cardDefault", { last4: pm.last4 })
                        : t("bulkShip.card", { last4: pm.last4 })}
                    </SelectItem>
                  ))}
                  <SelectItem key="decide-later">
                    {t("bulkShip.decideLater")}
                  </SelectItem>
                  {hasNetTermsAccess && !hasUnpaidInvoices ? (
                    <SelectItem key="invoice">
                      {t("bulkShip.netTermsCredit", {
                        amount: formatCurrency(availableCredit),
                      })}
                    </SelectItem>
                  ) : null}
                  {hasNetTermsAccess && hasUnpaidInvoices ? (
                    <SelectItem
                      key="invoice-disabled"
                      className="text-amber-600"
                    >
                      {t("bulkShip.netTermsPayFirst")}
                    </SelectItem>
                  ) : null}
                </>
              </Select>

              <div className="flex items-center gap-3">
                <Button variant="bordered" onClick={onClose}>
                  {t("actions.cancel")}
                </Button>
                <Button
                  onClick={startProcessing}
                  disabled={readyCount === 0 || isLoading}
                  className="min-w-[150px]"
                >
                  {isLoading ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      {t("bulkShip.loading")}
                    </>
                  ) : (
                    t("bulkShip.createShipments", { count: readyCount })
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Per-row pickup edit popover */}
      {editPickupForOrderId && pickupConfig && (
        <PickupEditDialog
          order={orders.find((o) => o.id === editPickupForOrderId) ?? null}
          pickupConfig={pickupConfig}
          onClose={() => setEditPickupForOrderId(null)}
          onSave={(pickup) => {
            setOrders((prev) =>
              prev.map((o) =>
                o.id === editPickupForOrderId ? { ...o, pickup } : o
              )
            );
            setEditPickupForOrderId(null);
          }}
        />
      )}
    </div>
  );
}

interface PickupEditDialogProps {
  order: ParsedOrder | null;
  pickupConfig: BulkPickupConfig;
  onClose: () => void;
  onSave: (pickup: { date: string; slotId: string } | null) => void;
}

function PickupEditDialog({
  order,
  pickupConfig,
  onClose,
  onSave,
}: PickupEditDialogProps) {
  const { t } = useTranslation("app");
  const [mode, setMode] = useState<"dropoff" | "pickup">(
    order?.pickup ? "pickup" : "dropoff"
  );
  const [date, setDate] = useState(order?.pickup?.date ?? "");
  const [slotId, setSlotId] = useState(order?.pickup?.slotId ?? "");

  const handleApply = () => {
    if (mode === "dropoff") {
      onSave(null);
      return;
    }
    if (!date || !slotId) return;
    onSave({ date, slotId });
  };

  return (
    <Modal
      isOpen={!!order}
      onOpenChange={(open: boolean) => !open && onClose()}
      size="md"
    >
      <ModalContent>
        {(modalClose) => (
          <>
            <ModalHeader>
              {order?.recipientName
                ? t("bulkShip.pickupFor", { name: order.recipientName })
                : t("bulkShip.pickupForRow")}
            </ModalHeader>
            <ModalBody className="space-y-4 py-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="rowPickupMode"
                  className="mt-1"
                  checked={mode === "dropoff"}
                  onChange={() => setMode("dropoff")}
                />
                <div>
                  <div className="text-sm font-medium">
                    {t("bulkShip.dropoffNoPickup")}
                  </div>
                  <div className="text-xs text-default-500">
                    {t("bulkShip.dropoffDescription")}
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="rowPickupMode"
                  className="mt-1"
                  checked={mode === "pickup"}
                  onChange={() => setMode("pickup")}
                />
                <div className="flex-1">
                  <div className="text-sm font-medium">
                    {t("bulkShip.pickupAtSender")}
                  </div>
                  {mode === "pickup" && (
                    <div className="mt-2 space-y-2">
                      <div>
                        <label className="text-xs font-medium text-default-700">
                          {t("bulkShip.date")}
                        </label>
                        <Input
                          type="date"
                          value={date}
                          onChange={(e) => setDate(e.target.value)}
                          min={new Date().toISOString().split("T")[0]}
                          size="sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-default-700">
                          {t("bulkShip.timeSlot")}
                        </label>
                        <Select
                          size="sm"
                          aria-label={t("bulkShip.timeSlot")}
                          placeholder={t("bulkShip.selectTimeSlot")}
                          selectedKeys={slotId ? [slotId] : []}
                          onSelectionChange={(keys) => {
                            const v = Array.from(keys as Set<string>)[0];
                            if (v) setSlotId(v);
                          }}
                        >
                          {pickupConfig.timeSlots.map((s) => (
                            <SelectItem key={s.id}>
                              {`${s.name} (${s.startTime}–${s.endTime})`}
                            </SelectItem>
                          ))}
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              </label>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={modalClose}>
                {t("actions.cancel")}
              </Button>
              <Button
                color="primary"
                onPress={handleApply}
                isDisabled={mode === "pickup" && (!date || !slotId)}
              >
                {t("bulkShip.apply")}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
